/**
 * Safe JSON parser that handles LLM output quirks.
 * Extracts JSON from markdown code blocks, raw strings, and handles
 * malformed responses gracefully.
 */
import { logger } from "@/app/lib/logger";

const MAX_CONTENT_LENGTH = 10 * 1024 * 1024; // 10MB limit

export function safeJsonParse(content: string | null | undefined): any {
   if (!content) return {};

   if (content.length > MAX_CONTENT_LENGTH) {
      logger.error(
         `[safeJsonParse] Content too large: ${content.length} bytes, max: ${MAX_CONTENT_LENGTH}`
      );
      return {};
   }

   try {
      const parsed = JSON.parse(content);
      if (typeof parsed !== "object" || parsed === null) {
         logger.warn("[safeJsonParse] Parsed result is not an object:", typeof parsed);
         return {};
      }
      return parsed;
   } catch (parseError) {
      try {
         // Try to extract JSON from markdown code blocks (supports both objects and arrays)
         const jsonMatch =
            content.match(/```(?:json)?\s*(\[[\s\S]*?\])\s*```/) ||
            content.match(/```(?:json)?\s*(\{[\s\S]*?\})\s*```/);
         if (jsonMatch && jsonMatch[1]) {
            const extracted = jsonMatch[1];
            if (extracted.length > MAX_CONTENT_LENGTH) {
               logger.error(
                  `[safeJsonParse] Extracted JSON too large: ${extracted.length} bytes`
               );
               return {};
            }
            const parsed = JSON.parse(extracted);
            if (typeof parsed !== "object" || parsed === null) {
               return {};
            }
            return parsed;
         } else {
            // No code block wrapper. Try to extract the JSON payload directly
            // from the raw content (e.g. when the LLM prefixes with prose like
            // "Here is the JSON: {...}").
            //
            // Try OBJECT extraction FIRST, then fall back to ARRAY. Otherwise
            // an object that contains array values (e.g. { "subQueries": [...] })
            // would get stripped to just the inner array, breaking callers
            // that expect the surrounding object shape.
            const hasObject = content.includes("{") && content.includes("}");
            const hasArray = content.includes("[") && content.includes("]");

            const tryParseCandidate = (candidate: string): object | null => {
               if (candidate.length > MAX_CONTENT_LENGTH) {
                  logger.error(
                     `[safeJsonParse] Extracted JSON too large: ${candidate.length} bytes`
                  );
                  return null;
               }
               try {
                  const parsed = JSON.parse(candidate);
                  if (typeof parsed !== "object" || parsed === null) {
                     return null;
                  }
                  return parsed;
               } catch {
                  return null;
               }
            };

            const candidates: string[] = [];
            if (hasObject) {
               candidates.push(
                  content.substring(content.indexOf("{"), content.lastIndexOf("}") + 1)
               );
            }
            if (hasArray) {
               candidates.push(
                  content.substring(content.indexOf("["), content.lastIndexOf("]") + 1)
               );
            }

            for (const candidate of candidates) {
               const parsed = tryParseCandidate(candidate);
               if (parsed !== null) return parsed;
            }

            throw new Error("Could not extract valid JSON");
         }
      } catch (extractError) {
         const errorMessage =
            extractError instanceof Error ? extractError.message : String(extractError);
         const contentPreview = content.substring(0, 300);

         logger.error(
            "[safeJsonParse] Failed to extract JSON:",
            errorMessage,
            "Content length:",
            content.length
         );
         // Dev-only: show raw content preview for debugging
         logger.debug("[safeJsonParse] Raw content preview:", contentPreview);
         return {};
      }
   }
}
